import {
  CoreFlightsRebookComponent,
  RebookImportantInfo,
  useFlightsRebookStyles,
} from "@b2bportal/core-flights-rebook";
import { useModuleBEM } from "@b2bportal/core-themes";
import { DateTimeFormatStyle, Trans, useI18nContext } from "@hopper-b2b/i18n";
import { ReviewPage } from "@tenant/flights";
import {
  RebookBookingOverlay,
  type RebookReviewPageProps,
} from "@tenant/flights-rebook";
import { InfoBanner, InfoBannerSeverity, Typography } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./RebookReviewPage.module.scss";
import { DockedButton } from "@/components/DockedButton";

const COMPONENT_KEY = CoreFlightsRebookComponent.RebookReviewPage;
export const RebookReviewPage = (props: RebookReviewPageProps) => {
  const styles = useFlightsRebookStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const {
    context: { travelers, returnSlice, isRoundTrip, rebookButtonDisabled },
    handlers: { onInitiateRebook, onRebookSuccess },
  } = props;

  const { t, formatDateTime } = useI18nContext();

  return (
    <ReviewPage {...props} className={clsx(block, props.className)}>
      {!isRoundTrip ? (
        <InfoBanner
          className={cn("child", ["notice"])}
          severity={InfoBannerSeverity.INFO}
        >
          <Typography
            type="heading"
            weight="light"
            size="xs"
            className={cn("notice-title")}
          >
            {t("core-flights.rebook.summary.notice.title")}
          </Typography>
          <ul className={cn("notice-list")}>
            {returnSlice != null ? (
              <Typography
                type="paragraph"
                size="md"
                className={cn("notice-item")}
                component="li"
              >
                <Trans
                  i18nKey="core-flights.rebook.review.notice.bullets.departure-rebook"
                  components={{
                    bold: <span className={cn("bold")} />,
                  }}
                  values={{
                    originName: returnSlice.origin.displayName,
                    originCode: returnSlice.origin.code,
                    destinationName: returnSlice.destination.displayName,
                    destinationCode: returnSlice.destination.code,
                    departureDate: formatDateTime(
                      new Date(returnSlice.departureDate),
                      DateTimeFormatStyle.MediumDate
                    ),
                  }}
                />
              </Typography>
            ) : null}
            <Typography
              type="paragraph"
              size="md"
              className={cn("notice-item")}
              component="li"
            >
              {t("core-flights.rebook.review.notice.bullets.one-way-flight")}
            </Typography>
            <Typography
              type="paragraph"
              size="md"
              className={cn("notice-item")}
              component="li"
            >
              {t("core-flights.rebook.review.notice.bullets.baggage-recheck")}
            </Typography>
          </ul>
        </InfoBanner>
      ) : null}
      <div className={cn("child", ["travelers"])}>
        <Typography
          className={cn("travelers-heading")}
          type="heading"
          size="xs"
          weight="light"
        >
          {t("core-flights.rebook.review.travelers-heading")}
        </Typography>
        {travelers != null ? (
          <ul className={cn("travelers-list")}>
            {travelers.map((traveler, index) => (
              <Typography
                key={index}
                type="paragraph"
                size="md"
                variant="secondary"
                component="li"
              >
                {traveler}
              </Typography>
            ))}
          </ul>
        ) : null}
      </div>
      <RebookImportantInfo className={cn("child", ["important-info"])} />
      <DockedButton
        primaryButtonOnClick={onInitiateRebook}
        primaryButtonLabel={t("core-flights.rebook.review.button")}
        primaryButtonDisabled={rebookButtonDisabled}
      />
      <RebookBookingOverlay onSuccess={onRebookSuccess} />
    </ReviewPage>
  );
};
